import { useEffect } from "react";
import Screen from "../components/Screen";
import { GhostBtn } from "../components/Buttons";
import ScanViewport from "../components/ScanViewport";
import { isPracticeMode } from "@utils/practice/practiceMode";

interface QrBarcodeProps {
  onScan: (code: string) => void;
  onCantScan: () => void;
  /** 省略＝沒有上一步（掃碼進來的第一頁）→ 不顯示返回鍵。 */
  onBack?: () => void;
  /** 正在向後端查詢 barcode 狀態（GET /register/cylinder-by-barcode）→ 停用清單、顯示查詢中提示。 */
  scanning?: boolean;
  /** 顯示示範樣本卡（僅 demo/DevHome 展示；真實入口＝已解析瓦斯行時必須隱藏，避免 mock 外洩）。 */
  showSamples?: boolean;
}

// 示範用 barcode 樣本：實際狀態由後端 GET /register/cylinder-by-barcode 回傳（正式版由相機讀碼取代）
const SAMPLES: Array<{ code: string; tag: string }> = [
  { code: "TW-2024-8801", tag: "比對到客戶" },
  { code: "TW-2024-8802", tag: "此鋼瓶已被註冊" },
  { code: "TW-2024-8803", tag: "barcode 已失效" },
  { code: "TW-2024-8804", tag: "查無關聯（新客戶）" },
];

const QrBarcode = ({ onScan, onCantScan, onBack, scanning, showSamples = false }: QrBarcodeProps) => {
  // 練習模式：不顯示示範樣本按鈕（避免客服誤以為正式 App 也長這樣），
  // 改成像真的掃描——顯示「掃描中…」後自動模擬相機掃到一支「比對到客戶」的鋼瓶。
  const practice = isPracticeMode();
  useEffect(() => {
    if (!practice) return;
    const t = setTimeout(() => onScan("TW-2024-8801"), 2600);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [practice]);

  return (
    <Screen
      title="掃描 barcode"
      subtitle={scanning ? "查詢中，請稍候…" : "對準鋼瓶頸部的鐵牌 barcode，辨識鋼瓶與您的資料"}
      /* 練習模式：掃描頁是掃實體 QR 的落地起點，隱藏左上返回鍵（正式流程維持原本可返回）。 */
      onBack={practice ? undefined : onBack}
      footer={<GhostBtn onClick={onCantScan}>掃不到條碼？改用手機號碼繼續</GhostBtn>}
    >
      <div className="h-full flex flex-col">
        <ScanViewport fill onDecode={(code) => !scanning && onScan(code)}>
          {practice ? (
            /* 練習：模擬掃描中（正式 App 也是這個畫面，只有相機在掃） */
            <div className="absolute left-0 right-0 bottom-5 flex justify-center">
              <div className="flex items-center gap-2 bg-black/55 rounded-full px-4 py-2">
                <span className="w-2 h-2 rounded-full bg-[#7fc0ff] animate-pulse" />
                <span className="text-[13px] text-white font-semibold">掃描中…</span>
              </div>
            </div>
          ) : showSamples ? (
            /* 示範樣本：僅 demo/DevHome（真實入口＝已解析瓦斯行時隱藏，避免 mock 外洩） */
            <div
              className="absolute left-0 right-0 bottom-0 px-3 pt-8 pb-3"
              style={{ background: "linear-gradient(to top, rgba(8,12,18,0.92) 55%, rgba(8,12,18,0))" }}
            >
              <div className="text-[11.5px] text-white/60 font-semibold mb-2 px-0.5">示範：模擬掃到不同的鋼瓶</div>
              <div className="grid grid-cols-2 gap-2">
                {SAMPLES.map((s) => (
                  <button
                    key={s.code}
                    onClick={() => onScan(s.code)}
                    className="flex items-center gap-2 text-left bg-white/12 border border-white/20 rounded-[12px] px-3 py-2 cursor-pointer"
                  >
                    <div className="w-7 h-7 rounded-[8px] bg-white/15 flex items-center justify-center shrink-0">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                        <path d="M4 6v12M8 6v12M12 6v12M16 6v12M20 6v12" stroke="#7fc0ff" strokeWidth="1.7" strokeLinecap="round" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-bold text-white leading-tight truncate">{s.tag}</div>
                      <div className="text-[10px] text-white/45 mt-0.5 truncate">{s.code}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </ScanViewport>
      </div>
    </Screen>
  );
};

export default QrBarcode;
